import React, { useState, useEffect, useRef } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useAuth } from 'contexts/AuthContext';
import { supabase } from 'lib/supabase';
import AdminDashboardLayout from 'pages/admin-dashboard-overview/components/AdminDashboardLayout';

// ─── Google Fonts ──────────────────────────────────────────────────────────────
const FONT_LINK_ID = 'volaptic-admin-fonts';
const FONT_STYLE_ID = 'volaptic-admin-font-style';
const STORAGE_KEY_FONT = 'volaptic_admin_font';

const FONTS = [
  { id: 'default', label: 'Default', family: null, description: 'Font di sistema' },
  { id: 'plus-jakarta-sans', label: 'Plus Jakarta Sans', family: "'Plus Jakarta Sans', sans-serif", googleName: 'Plus+Jakarta+Sans:wght@400;500;600;700' },
  { id: 'dm-sans', label: 'DM Sans', family: "'DM Sans', sans-serif", googleName: 'DM+Sans:wght@400;500;600;700' },
  { id: 'outfit', label: 'Outfit', family: "'Outfit', sans-serif", googleName: 'Outfit:wght@400;500;600;700' },
];

const injectGoogleFonts = () => {
  if (document.getElementById(FONT_LINK_ID)) return;
  const fontNames = FONTS?.filter(f => f?.googleName)?.map(f => f?.googleName)?.join('&family=');
  const link = document.createElement('link');
  link.id = FONT_LINK_ID;
  link.rel = 'stylesheet';
  link.href = `https://fonts.googleapis.com/css2?family=${fontNames}&display=swap`;
  document.head?.appendChild(link);
};

const applyAdminFont = (fontId) => {
  let styleEl = document.getElementById(FONT_STYLE_ID);
  if (!styleEl) {
    styleEl = document.createElement('style');
    styleEl.id = FONT_STYLE_ID;
    document.head?.appendChild(styleEl);
  }
  const font = FONTS?.find(f => f?.id === fontId);
  if (font?.family) {
    styleEl.textContent = `body { font-family: ${font?.family} !important; }`;
  } else {
    styleEl.textContent = `body { font-family: -apple-system, BlinkMacSystemFont, 'Segoe UI', Roboto, sans-serif !important; }`;
  }
};

// ─── Toast component ───────────────────────────────────────────────────────────
const Toast = ({ message, type = 'success', onClose }) => {
  useEffect(() => {
    const t = setTimeout(onClose, 3500);
    return () => clearTimeout(t);
  }, [onClose]);

  const bg = type === 'success' ?'bg-green-50 border-green-200 text-green-700' :'bg-red-50 border-red-200 text-red-700';

  return (
    <div className={`fixed bottom-6 right-6 z-50 flex items-center gap-3 px-4 py-3 rounded-xl border shadow-lg text-sm font-medium ${bg}`}>
      {type === 'success' ? (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <polyline points="20 6 9 17 4 12"/>
        </svg>
      ) : (
        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
          <circle cx="12" cy="12" r="10"/><line x1="12" y1="8" x2="12" y2="12"/><line x1="12" y1="16" x2="12.01" y2="16"/>
        </svg>
      )}
      {message}
    </div>
  );
};

// ─── Main Page ─────────────────────────────────────────────────────────────────
const AdminSettings = () => {
  const navigate = useNavigate();
  const { user, userProfile, loading: authLoading } = useAuth();

  // Profile
  const [fullName, setFullName] = useState('');
  const [email, setEmail] = useState('');
  const [profileSaving, setProfileSaving] = useState(false);

  // Avatar (photo-only)
  const [photoUrl, setPhotoUrl] = useState(null);
  const [photoUploading, setPhotoUploading] = useState(false);
  const photoInputRef = useRef(null);

  // Font
  const [selectedFont, setSelectedFont] = useState('default');
  const [fontSaving, setFontSaving] = useState(false);

  // Platform config — show_payment_skip_banner
  const [showPaymentSkipBanner, setShowPaymentSkipBanner] = useState(true);
  const [bannerToggleSaving, setBannerToggleSaving] = useState(false);

  // Password
  const [passwordForm, setPasswordForm] = useState({ current: '', newPass: '', confirm: '' });
  const [passwordError, setPasswordError] = useState('');
  const [passwordSuccess, setPasswordSuccess] = useState('');
  const [passwordLoading, setPasswordLoading] = useState(false);

  // Toast
  const [toast, setToast] = useState(null);
  const showToast = (message, type = 'success') => setToast({ message, type });

  const [pageLoading, setPageLoading] = useState(true);

  // Inject Google Fonts on mount
  useEffect(() => {
    injectGoogleFonts();
  }, []);

  // Load persisted font from localStorage
  useEffect(() => {
    const savedFont = localStorage.getItem(STORAGE_KEY_FONT);
    if (savedFont) {
      setSelectedFont(savedFont);
      applyAdminFont(savedFont);
    }
  }, []);

  // Auth guard + data fetch
  useEffect(() => {
    if (authLoading) return;
    if (!user) { navigate('/login-form'); return; }

    const loadData = async () => {
      setPageLoading(true);
      try {
        const { data: { user: freshUser } } = await supabase?.auth?.getUser();
        if (!freshUser) { navigate('/login-form'); return; }

        const { data: userData } = await supabase
          ?.from('users')
          ?.select('email, full_name, avatar_url, role')
          ?.eq('id', freshUser?.id)
          ?.single();

        if (userData) {
          setEmail(userData?.email || freshUser?.email || '');
          setFullName(userData?.full_name || '');
          if (userData?.avatar_url) {
            setPhotoUrl(userData?.avatar_url);
          }
        } else {
          setEmail(freshUser?.email || '');
        }

        // Load platform_config toggle
        const { data: configData } = await supabase
          ?.from('platform_config')
          ?.select('value')
          ?.eq('key', 'show_payment_skip_banner')
          ?.single();
        if (configData) {
          setShowPaymentSkipBanner(configData?.value === true || configData?.value === 'true');
        }
      } catch (err) {
        console.error('Error loading admin settings:', err);
      } finally {
        setPageLoading(false);
      }
    };

    loadData();
  }, [user, authLoading, navigate]);

  // ── Profile save ─────────────────────────────────────────────────────────────
  const handleProfileSave = async (e) => {
    e?.preventDefault();
    setProfileSaving(true);
    try {
      // CRITICAL FIX 2: always use fresh user
      const { data: { user: freshUser }, error: authErr } = await supabase?.auth?.getUser();
      if (authErr || !freshUser) throw new Error('Not authenticated');
      const { error } = await supabase
        ?.from('users')
        ?.update({ full_name: fullName?.trim() })
        ?.eq('id', freshUser?.id);
      if (error) throw error;
      // Refresh auth context via custom event
      window.dispatchEvent(new CustomEvent('volaptic-name-update', {
        detail: { name: fullName?.trim() || '' }
      }));
      window.dispatchEvent(new CustomEvent('volaptic:profile-updated'));
      showToast('Nome salvato');
    } catch (err) {
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setProfileSaving(false);
    }
  };

  // ── Photo upload ─────────────────────────────────────────────────────────────
  // CRITICAL FIX 1: use bucket 'admin-avatars', path = {uid}.jpg (no subfolder)
  const handlePhotoUpload = async (e) => {
    const file = e?.target?.files?.[0];
    if (!file) return;

    // Validate type
    if (!['image/jpeg', 'image/png', 'image/webp']?.includes(file?.type)) {
      showToast('Formato non supportato. Usa JPEG, PNG o WebP.', 'error');
      return;
    }
    // Validate size (2MB)
    if (file?.size > 2 * 1024 * 1024) {
      showToast('Il file supera i 2MB.', 'error');
      return;
    }

    setPhotoUploading(true);
    try {
      // CRITICAL FIX 2: fresh user
      const { data: { user: freshUser }, error: authErr } = await supabase?.auth?.getUser();
      if (authErr || !freshUser) throw new Error('Not authenticated');

      // CRITICAL FIX 1: path = {uid}.jpg directly under bucket root
      const path = `${freshUser?.id}.jpg`;

      const { error: upErr } = await supabase?.storage
        ?.from('admin-avatars')
        ?.upload(path, file, { upsert: true, contentType: file?.type });
      if (upErr) throw upErr;

      const { data: urlData } = supabase?.storage
        ?.from('admin-avatars')
        ?.getPublicUrl(path);

      // Cache-busting
      const finalUrl = `${urlData?.publicUrl}?v=${Date.now()}`;

      const { error: dbErr } = await supabase
        ?.from('users')
        ?.update({ avatar_url: finalUrl, avatar_icon: null })
        ?.eq('id', freshUser?.id);
      if (dbErr) throw dbErr;

      setPhotoUrl(finalUrl);

      // Refresh auth context
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: null, url: finalUrl }
      }));
      showToast('Foto caricata');
    } catch (err) {
      showToast(err?.message || 'Errore durante il caricamento.', 'error');
    } finally {
      setPhotoUploading(false);
      if (photoInputRef?.current) photoInputRef.current.value = '';
    }
  };

  const handleRemovePhoto = async () => {
    try {
      // CRITICAL FIX 2: fresh user
      const { data: { user: freshUser }, error: authErr } = await supabase?.auth?.getUser();
      if (authErr || !freshUser) throw new Error('Not authenticated');

      // Best-effort storage delete
      try {
        await supabase?.storage?.from('admin-avatars')?.remove([`${freshUser?.id}.jpg`]);
      } catch (_) {
        // ignore storage delete errors
      }

      const { error: dbErr } = await supabase
        ?.from('users')
        ?.update({ avatar_url: null })
        ?.eq('id', freshUser?.id);
      if (dbErr) throw dbErr;

      setPhotoUrl(null);

      // Refresh auth context
      window.dispatchEvent(new CustomEvent('volaptic-avatar-update', {
        detail: { icon: null, url: null }
      }));
      showToast('Foto rimossa');
    } catch (err) {
      showToast(err?.message || 'Errore durante la rimozione', 'error');
    }
  };

  // ── Font save ────────────────────────────────────────────────────────────────
  const handleFontSave = () => {
    setFontSaving(true);
    localStorage.setItem(STORAGE_KEY_FONT, selectedFont);
    applyAdminFont(selectedFont);
    setTimeout(() => {
      setFontSaving(false);
      showToast('Preferenze salvate');
    }, 300);
  };

  // ── Banner toggle ────────────────────────────────────────────────────────────
  const handleBannerToggle = async () => {
    const newValue = !showPaymentSkipBanner;
    setBannerToggleSaving(true);
    try {
      const { error } = await supabase
        ?.from('platform_config')
        ?.upsert({ key: 'show_payment_skip_banner', value: newValue }, { onConflict: 'key' });
      if (error) throw error;
      setShowPaymentSkipBanner(newValue);
      showToast(newValue ? 'Banner attivato' : 'Banner disattivato');
    } catch (err) {
      showToast(err?.message || 'Errore durante il salvataggio', 'error');
    } finally {
      setBannerToggleSaving(false);
    }
  };

  // ── Password change ──────────────────────────────────────────────────────────
  const handlePasswordSubmit = async (e) => {
    e?.preventDefault();
    setPasswordError('');
    setPasswordSuccess('');

    if (passwordForm?.newPass !== passwordForm?.confirm) {
      setPasswordError('Le password non coincidono');
      return;
    }
    if (passwordForm?.newPass?.length < 8) {
      setPasswordError('La nuova password deve essere di almeno 8 caratteri');
      return;
    }

    setPasswordLoading(true);

    // CRITICAL FIX 2: fresh user for email
    const { data: { user: freshUser } } = await supabase?.auth?.getUser();
    const userEmail = freshUser?.email || email || user?.email;

    const { error: signInError } = await supabase?.auth?.signInWithPassword({
      email: userEmail,
      password: passwordForm?.current,
    });

    if (signInError) {
      setPasswordError('Password attuale non corretta');
      setPasswordLoading(false);
      return;
    }

    const { error: updateError } = await supabase?.auth?.updateUser({ password: passwordForm?.newPass });

    if (updateError) {
      setPasswordError(updateError?.message || "Errore durante l'aggiornamento della password");
    } else {
      setPasswordSuccess('Password aggiornata con successo');
      setPasswordForm({ current: '', newPass: '', confirm: '' });
      showToast('Password aggiornata con successo');
    }

    setPasswordLoading(false);
  };

  // ── Avatar initials ──────────────────────────────────────────────────────────
  const getInitials = () => {
    if (fullName?.trim()) {
      return fullName?.trim()?.split(/\s+/)?.slice(0, 2)?.map(p => p?.[0]?.toUpperCase())?.join('') || '?';
    }
    return (email || '?')?.[0]?.toUpperCase();
  };

  if (authLoading || pageLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gray-50">
        <div className="w-12 h-12 border-4 border-blue-600 border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <AdminDashboardLayout>
      <div className="max-w-[720px] mx-auto px-4 py-10">

        {/* Page header */}
        <div className="mb-8">
          <h1 className="text-2xl font-bold text-gray-900">Impostazioni Admin</h1>
          <p className="text-sm text-gray-500 mt-1">Gestisci il tuo profilo amministratore</p>
        </div>

        {/* ── SECTION 1: PROFILO AMMINISTRATORE ─────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Profilo amministratore</h2>
            <form onSubmit={handleProfileSave} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Email</label>
                <input
                  type="email"
                  value={email}
                  readOnly
                  className="w-full px-3 py-2 border border-gray-200 rounded-lg text-sm bg-gray-50 text-gray-500 cursor-not-allowed"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nome completo</label>
                <input
                  type="text"
                  value={fullName}
                  onChange={(e) => setFullName(e?.target?.value)}
                  placeholder="Il tuo nome completo"
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <button
                type="submit"
                disabled={profileSaving}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#104d98' }}
              >
                {profileSaving ? 'Salvataggio...' : 'Salva nome'}
              </button>
            </form>
          </div>
        </section>

        {/* ── SECTION 2: IL TUO AVATAR (photo-only, no icon grid) ───────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Il tuo avatar</h2>
            <p className="text-sm text-gray-500 mb-5">Carica una foto profilo. JPEG, PNG o WebP, max 2MB.</p>

            {/* Circular preview — centered */}
            <div className="flex flex-col items-center gap-4">
              <div style={{ position: 'relative' }}>
                {photoUrl ? (
                  <img
                    src={photoUrl}
                    alt="Avatar admin"
                    style={{
                      width: '96px', height: '96px', borderRadius: '50%',
                      objectFit: 'cover', flexShrink: 0,
                      boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                      border: '3px solid #e0f2fe',
                    }}
                  />
                ) : (
                  <div style={{
                    width: '96px', height: '96px', borderRadius: '50%',
                    background: 'linear-gradient(135deg, #32b3e5 0%, #013776 100%)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    flexShrink: 0,
                    boxShadow: '0 2px 12px rgba(0,0,0,0.15)',
                  }}>
                    <span style={{ color: '#fff', fontWeight: 700, fontSize: '28px', lineHeight: 1 }}>
                      {getInitials()}
                    </span>
                  </div>
                )}
              </div>

              {/* Buttons row */}
              <div className="flex items-center gap-3">
                <input
                  ref={photoInputRef}
                  type="file"
                  accept="image/jpeg,image/png,image/webp"
                  style={{ display: 'none' }}
                  onChange={handlePhotoUpload}
                />
                <button
                  type="button"
                  onClick={() => photoInputRef?.current?.click()}
                  disabled={photoUploading}
                  className="px-4 py-2 text-white rounded-lg transition-colors text-sm font-semibold disabled:opacity-50"
                  style={{ backgroundColor: '#2780b5' }}
                  onMouseEnter={e => e.currentTarget.style.backgroundColor = '#013776'}
                  onMouseLeave={e => e.currentTarget.style.backgroundColor = '#2780b5'}
                >
                  {photoUploading ? 'Caricamento...' : 'Cambia foto'}
                </button>
                {photoUrl && (
                  <button
                    type="button"
                    onClick={handleRemovePhoto}
                    className="px-4 py-2 text-gray-500 hover:text-red-600 hover:bg-red-50 rounded-lg transition-colors text-sm font-medium"
                  >
                    Rimuovi foto
                  </button>
                )}
              </div>
            </div>
          </div>
        </section>

        {/* ── SECTION 3: ASPETTO ────────────────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-1">Aspetto</h2>
            <p className="text-sm text-gray-500 mb-5">Scegli il font per le pagine admin.</p>
            <div className="space-y-3 mb-5">
              {FONTS?.map((font) => {
                const isSelected = selectedFont === font?.id;
                return (
                  <label
                    key={font?.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: '14px',
                      padding: '14px 16px',
                      borderRadius: '10px',
                      border: isSelected ? '2px solid #32b3e5' : '1.5px solid #e5e7eb',
                      background: isSelected ? '#f0f9ff' : '#fff',
                      cursor: 'pointer',
                      transition: 'all 0.15s',
                    }}
                  >
                    <input
                      type="radio"
                      name="admin-font-picker"
                      value={font?.id}
                      checked={isSelected}
                      onChange={() => setSelectedFont(font?.id)}
                      style={{ accentColor: '#32b3e5', width: '16px', height: '16px', flexShrink: 0 }}
                    />
                    <div style={{ flex: 1 }}>
                      <div style={{ fontSize: '13px', fontWeight: 600, color: '#374151', marginBottom: '4px' }}>
                        {font?.label}
                        {font?.description && (
                          <span style={{ fontWeight: 400, color: '#9ca3af', marginLeft: '6px', fontSize: '12px' }}>
                            — {font?.description}
                          </span>
                        )}
                      </div>
                      <div
                        style={{
                          fontFamily: font?.family || '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
                          fontSize: '15px',
                          color: isSelected ? '#32b3e5' : '#6b7280',
                          fontWeight: 400,
                        }}
                      >
                        Testo di esempio
                      </div>
                    </div>
                  </label>
                );
              })}
            </div>
            <button
              type="button"
              onClick={handleFontSave}
              disabled={fontSaving}
              className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
              style={{ backgroundColor: '#104d98' }}
            >
              {fontSaving ? 'Salvataggio...' : 'Salva preferenze'}
            </button>
          </div>
        </section>

        {/* ── SECTION 3b: CONFIGURAZIONE PIATTAFORMA ────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Configurazione piattaforma</h2>

            {/* Toggle row */}
            <div className="flex items-center justify-between gap-4">
              <div className="flex-1">
                <p className="text-sm font-medium text-gray-800">
                  Mostra banner &ldquo;salta verifica carta&rdquo;
                </p>
              </div>
              {/* Toggle switch */}
              <button
                type="button"
                role="switch"
                aria-checked={showPaymentSkipBanner}
                disabled={bannerToggleSaving}
                onClick={handleBannerToggle}
                className={`relative inline-flex h-6 w-11 flex-shrink-0 cursor-pointer rounded-full border-2 border-transparent transition-colors duration-200 ease-in-out focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 disabled:opacity-50 disabled:cursor-not-allowed ${
                  showPaymentSkipBanner ? 'bg-blue-600' : 'bg-gray-200'
                }`}
              >
                <span
                  className={`pointer-events-none inline-block h-5 w-5 transform rounded-full bg-white shadow ring-0 transition duration-200 ease-in-out ${
                    showPaymentSkipBanner ? 'translate-x-5' : 'translate-x-0'
                  }`}
                />
              </button>
            </div>

            {/* Helper text */}
            <p className="mt-3 text-xs text-gray-400 leading-relaxed">
              Quando attivo, i professionisti in registrazione vedono un banner che spiega che possono saltare la verifica della carta perché il loro profilo è già riconosciuto come genuino — riduce l&apos;abbandono senza perdere la protezione anti-bot per i profili incompleti.
            </p>
          </div>
        </section>

        {/* ── SECTION 4: MODIFICA PASSWORD ──────────────────────────────────── */}
        <section className="mb-6">
          <div className="bg-white rounded-xl border border-gray-200 shadow-sm p-6">
            <h2 className="text-lg font-semibold text-gray-900 mb-5">Modifica password</h2>
            <form onSubmit={handlePasswordSubmit} className="space-y-4">
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Password attuale</label>
                <input
                  type="password"
                  required
                  value={passwordForm?.current}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, current: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Nuova password</label>
                <input
                  type="password"
                  required
                  minLength={8}
                  value={passwordForm?.newPass}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, newPass: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700 mb-1">Conferma nuova password</label>
                <input
                  type="password"
                  required
                  value={passwordForm?.confirm}
                  onChange={(e) => setPasswordForm((prev) => ({ ...prev, confirm: e?.target?.value }))}
                  className="w-full px-3 py-2 border border-gray-300 rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-blue-500 focus:border-transparent"
                />
              </div>

              {passwordError && (
                <p className="text-sm text-red-600 bg-red-50 border border-red-200 rounded-lg px-3 py-2">{passwordError}</p>
              )}
              {passwordSuccess && (
                <p className="text-sm text-green-700 bg-green-50 border border-green-200 rounded-lg px-3 py-2">{passwordSuccess}</p>
              )}

              <button
                type="submit"
                disabled={passwordLoading}
                className="inline-flex items-center gap-2 px-5 py-2.5 rounded-lg text-sm font-semibold text-white transition-colors disabled:opacity-60"
                style={{ backgroundColor: '#104d98' }}
              >
                {passwordLoading ? 'Aggiornamento...' : 'Aggiorna password'}
              </button>
            </form>
          </div>
        </section>

        {/* ── Back to dashboard ──────────────────────────────────────────────── */}
        <Link
          to="/admin"
          className="flex items-center justify-center gap-2 w-full mt-8 py-3 rounded-xl border border-gray-300 bg-white text-sm font-semibold text-gray-700 hover:bg-gray-50 hover:border-gray-400 shadow-sm transition-colors"
        >
          ← Torna alla dashboard
        </Link>
      </div>

      {/* Toast */}
      {toast && (
        <Toast
          message={toast?.message}
          type={toast?.type}
          onClose={() => setToast(null)}
        />
      )}
    </AdminDashboardLayout>
  );
};

export default AdminSettings;
